import { MF, PF } from "../components/NumberInputs";
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import Modal, { FormRow } from '../components/Modal';
import FinTable from "../components/FinTable";

/* local FormSection — not exported by Modal.js */
const FormSection = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#8892a5', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
    {children}
  </div>
);

/* ───────────────────────── palette / tokens ───────────────────────── */
const C = {
  bg:       '#0a0f1a',
  card:     '#111827',
  border:   '#1e2a3a',
  text:     '#e2e8f0',
  muted:    '#8892a5',
  accent:   '#3b82f6',
  green:    '#10b981',
  yellow:   '#f59e0b',
  red:      '#ef4444',
  purple:   '#a855f7',
  orange:   '#f97316',
  cyan:     '#06b6d4',
  rowHover: '#1a2332',
};

const s = {
  page:       { padding: 24, color: C.text, minHeight: '100vh', background: C.bg },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title:      { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle:   { color: C.muted, fontSize: 13, marginTop: 4 },
  section:    { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 24, overflow: 'hidden' },
  sectionHdr: { padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionTtl: { fontSize: 15, fontWeight: 600, margin: 0 },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:         { textAlign: 'left', padding: '10px 14px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  td:         { padding: '10px 14px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  btn:        { padding: '7px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#fff' },
  btnPrimary: { background: C.accent },
  btnSmall:   { padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#fff', background: C.accent },
  badge:      (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#fff', background: color }),
  money:      { fontFamily: '"SF Mono", "Fira Code", monospace', textAlign: 'right' },
  emptyRow:   { padding: 32, textAlign: 'center', color: C.muted, fontSize: 13 },
  input:      { width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13 },
  select:     { width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13 },
  textarea:   { width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' },
  kpiRow:     { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:    { flex: 1, minWidth: 180, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px' },
  kpiLabel:   { fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  kpiValue:   { fontSize: 20, fontWeight: 700 },
};

const fmt = (v) => v != null ? parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtPct = (v) => v != null ? `${parseFloat(v).toFixed(2)}%` : '—';
const fmtInt = (v) => v != null ? parseInt(v).toLocaleString() : '—';
const fmtDate = (v) => v || '—';

const statusColor = { Active: C.green, Pending: C.yellow, Expired: C.red, Terminated: C.red };
const noticeStatusColor = {
  NoticeReceived: C.accent,
  PricingWindow: C.yellow,
  Settled: C.green,
  Cancelled: C.red,
};
const noticeStatusLabel = {
  NoticeReceived: 'Notice Received',
  PricingWindow: 'Pricing Window',
  Settled: 'Settled',
  Cancelled: 'Cancelled',
};

/* ─────────────────────── ELOC FACILITIES PAGE ─────────────────────── */
export default function ElocFacilities() {
  const [securities, setSecurities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [taxLots, setTaxLots] = useState([]);
  const [putNotices, setPutNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* modals */
  const [editModal, setEditModal] = useState({ open: false, sec: null });
  const [cfModal, setCfModal] = useState({ open: false, deal: null });
  const [putModal, setPutModal] = useState({ open: false, deal: null, notice: null });
  const [editForm, setEditForm] = useState({});
  const [cfForm, setCfForm] = useState({});
  const [saving, setSaving] = useState(false);

  /* ── data fetch ───────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [secRes, dealRes, lotRes, noticeRes] = await Promise.all([
        api.get('/securities/securities/'),
        api.get('/securities/deals/'),
        api.get('/positions/tax-lots/'),
        api.get('/positions/eloc-notices/'),
      ]);
      const allSecs = secRes.data.results || secRes.data;
      const elocDeals = (dealRes.data.results || dealRes.data).filter(d => (d.strategy_tag || '') === 'ELOC');
      const elocSecIds = new Set(elocDeals.map(d => d.security));
      const secs = allSecs.filter(s => elocSecIds.has(s.id));
      const elocDealIds = new Set(elocDeals.map(d => d.id));
      const cfLots = (lotRes.data.results || lotRes.data).filter(
        l => elocDealIds.has(l.deal) && (l.action || '').toLowerCase().includes('cf')
      );
      const notices = (noticeRes.data.results || noticeRes.data).filter(
        n => elocDealIds.has(n.deal)
      );
      setSecurities(secs);
      setDeals(elocDeals);
      setTaxLots(cfLots);
      setPutNotices(notices);
    } catch (e) {
      console.error(e);
      setError('Failed to load ELOC data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── helpers ──────────────────────────────────────────────────── */
  const secById = Object.fromEntries(securities.map(s => [s.id, s]));
  const enrichedDeals = deals.map(d => ({ ...d, sec: secById[d.security] || {} }));
  const dealById = Object.fromEntries(deals.map(d => [d.id, d]));

  /* KPIs */
  const totalCommitment = securities.reduce((sum, s) => sum + parseFloat(s.eloc_commitment_amount || 0), 0);
  const activeFacilities = securities.filter(s => (s.eloc_facility_status || '').toLowerCase() === 'active').length;
  const totalCfShares = taxLots.reduce((sum, l) => sum + parseFloat(l.quantity || 0), 0);
  const activeNotices = putNotices.filter(n => n.status === 'NoticeReceived' || n.status === 'PricingWindow').length;
  const totalSettled = putNotices.filter(n => n.status === 'Settled').reduce((sum, n) => sum + parseFloat(n.settlement_amount || 0), 0);

  /* ── Edit Facility modal ─────────────────────────────────────── */
  const openEdit = (sec) => {
    setEditForm({
      eloc_commitment_amount:        sec.eloc_commitment_amount || '',
      eloc_discount_pct:             sec.eloc_discount_pct || '',
      eloc_discount_pct_2:           sec.eloc_discount_pct_2 || '',
      eloc_discount_pct_3:           sec.eloc_discount_pct_3 || '',
      eloc_pricing_days:             sec.eloc_pricing_days || '',
      eloc_pricing_method:           sec.eloc_pricing_method || '',
      eloc_pricing_method_2:         sec.eloc_pricing_method_2 || '',
      eloc_max_advance:              sec.eloc_max_advance || '',
      eloc_settlement_days:          sec.eloc_settlement_days || '',
      eloc_ownership_limit_pct:      sec.eloc_ownership_limit_pct || '',
      eloc_exchange_cap_pct:         sec.eloc_exchange_cap_pct || '',
      eloc_commitment_period_months: sec.eloc_commitment_period_months || '',
      eloc_commitment_fee_shares:    sec.eloc_commitment_fee_shares || '',
      eloc_commitment_fee_amount:    sec.eloc_commitment_fee_amount || '',
      eloc_investor:                 sec.eloc_investor || '',
      eloc_exchange:                 sec.eloc_exchange || '',
      eloc_s1_effective_date:        sec.eloc_s1_effective_date || '',
      eloc_facility_status:          sec.eloc_facility_status || 'Active',
      eloc_facility_notes:           sec.eloc_facility_notes || '',
    });
    setEditModal({ open: true, sec });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/securities/securities/${editModal.sec.id}/`, editForm);
      setEditModal({ open: false, sec: null });
      load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  /* ── Book CF Shares modal ────────────────────────────────────── */
  const openCf = (deal) => {
    setCfForm({
      deal: deal.id,
      security: deal.security,
      trade_date: new Date().toISOString().split('T')[0],
      action: 'CF_SHARES',
      quantity: deal.sec?.eloc_commitment_fee_shares || '',
      cost_basis: '0.00',
      unit_cost: '0.00',
    });
    setCfModal({ open: true, deal });
  };

  const saveCf = async () => {
    setSaving(true);
    try {
      await api.post('/positions/tax-lots/', {
        security: cfForm.security,
        deal: cfForm.deal,
        trade_date: cfForm.trade_date,
        action: cfForm.action,
        quantity: cfForm.quantity,
        cost_basis: cfForm.cost_basis,
        unit_cost: cfForm.unit_cost,
      });
      setCfModal({ open: false, deal: null });
      load();
    } catch (e) {
      alert('Book failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  /* ── Put Notice modal ────────────────────────────────────────── */
  const openPutNotice = (deal, existingNotice = null) => {
    const sec = secById[deal.security] || {};
    if (existingNotice) {
      setPutModal({ open: true, deal, notice: existingNotice, isEdit: true });
    } else {
      setPutModal({
        open: true, deal, notice: {
          deal: deal.id,
          entity: deal.entity || '',
          status: 'NoticeReceived',
          notice_date: new Date().toISOString().split('T')[0],
          requested_shares: '',
          drawdown_tier: 1,
          pricing_start: '',
          pricing_end: '',
          vwap: '',
          purchase_price: '',
          settlement_amount: '',
          settlement_date: '',
          notes: '',
        },
        isEdit: false,
      });
    }
  };

  const savePutNotice = async () => {
    setSaving(true);
    const data = { ...putModal.notice };
    // Clean empty strings to null for optional fields
    ['pricing_start', 'pricing_end', 'vwap', 'purchase_price', 'settlement_amount', 'settlement_date', 'notes', 'entity'].forEach(k => {
      if (data[k] === '') data[k] = null;
    });
    if (!data.drawdown_tier) data.drawdown_tier = null;

    try {
      if (putModal.isEdit && data.id) {
        await api.patch(`/positions/eloc-notices/${data.id}/`, data);
      } else {
        await api.post('/positions/eloc-notices/', data);
      }
      setPutModal({ open: false, deal: null, notice: null });
      load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  const updateNoticeField = (field, value) => {
    setPutModal(prev => ({
      ...prev,
      notice: { ...prev.notice, [field]: value },
    }));

    // Auto-calc settlement amount when VWAP or purchase price changes
    if (field === 'vwap' || field === 'purchase_price' || field === 'requested_shares') {
      setPutModal(prev => {
        const n = { ...prev.notice, [field]: value };
        const price = parseFloat(n.purchase_price) || parseFloat(n.vwap) || 0;
        const shares = parseFloat(n.requested_shares) || 0;
        if (price > 0 && shares > 0) {
          n.settlement_amount = (price * shares).toFixed(4);
        }
        return { ...prev, notice: n };
      });
    }
  };

  /* ── RENDER ──────────────────────────────────────────────────── */
  if (loading) return <div style={s.page}><p style={{ color: C.muted }}>Loading ELOC facilities…</p></div>;
  if (error) return <div style={s.page}><p style={{ color: C.red }}>{error}</p></div>;


  // ── Dropdown option mapping (Bug #14 fix) ──
  const dealOpts = deals.map(d => ({ value: d.id, label: d.deal_name || `Deal #${d.id}` }));
  const secOpts = securities.map(s => ({ value: s.id, label: s.ticker ? `${s.ticker} — ${s.name || s.instrument_type}` : s.name || `Security #${s.id}` }));

  return (
    <div style={s.page}>
      {/* header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>ELOC Facilities</h1>
          <div style={s.subtitle}>Equity Line of Credit agreements, put notices, and commitment fee shares</div>
        </div>
      </div>

      {/* KPIs */}
      <div style={s.kpiRow}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total Commitment</div>
          <div style={{ ...s.kpiValue, color: C.accent }}>${fmt(totalCommitment)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Active Facilities</div>
          <div style={{ ...s.kpiValue, color: C.green }}>{activeFacilities}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total Facilities</div>
          <div style={s.kpiValue}>{securities.length}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Active Put Notices</div>
          <div style={{ ...s.kpiValue, color: C.orange }}>{activeNotices}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total Settled</div>
          <div style={{ ...s.kpiValue, color: C.green }}>${fmt(totalSettled)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>CF Shares Booked</div>
          <div style={{ ...s.kpiValue, color: C.purple }}>{fmtInt(totalCfShares)}</div>
        </div>
      </div>

      {/* ── FACILITIES TABLE ───────────────────────────────────── */}
      {/* ── PUT NOTICES TABLE ──────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionHdr}>
          <h2 style={s.sectionTtl}>Put Notices</h2>
          <span style={{ fontSize: 12, color: C.muted }}>
            {putNotices.length} notice{putNotices.length !== 1 ? 's' : ''} · {activeNotices} active
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Notice Date</th>
                <th style={s.th}>Deal</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Tier</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Requested Shares</th>
                <th style={s.th}>Pricing Start</th>
                <th style={s.th}>Pricing End</th>
                <th style={{ ...s.th, textAlign: 'right' }}>VWAP</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Purchase Price</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Settlement Amt</th>
                <th style={s.th}>Settlement Date</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {putNotices.length === 0 ? (
                <tr><td colSpan={12} style={s.emptyRow}>No put notices yet. Click "Put Notice" on a facility to submit one.</td></tr>
              ) : putNotices.sort((a, b) => (b.notice_date || '').localeCompare(a.notice_date || '')).map(n => {
                const deal = dealById[n.deal];
                const sec = deal ? secById[deal.security] : null;
                const stColor = noticeStatusColor[n.status] || C.muted;
                return (
                  <tr key={n.id} onMouseEnter={e => e.currentTarget.style.background = C.rowHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={s.td}>{fmtDate(n.notice_date)}</td>
                    <td style={{ ...s.td, color: C.accent, fontWeight: 600 }}>{n.deal_name || deal?.deal_name || '—'}</td>
                    <td style={s.td}><span style={s.badge(stColor)}>{noticeStatusLabel[n.status] || n.status}</span></td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{n.drawdown_tier || '—'}</td>
                    <td style={{ ...s.td, ...s.money }}>{fmtInt(n.requested_shares)}</td>
                    <td style={s.td}>{fmtDate(n.pricing_start)}</td>
                    <td style={s.td}>{fmtDate(n.pricing_end)}</td>
                    <td style={{ ...s.td, ...s.money }}>${fmt(n.vwap)}</td>
                    <td style={{ ...s.td, ...s.money }}>${fmt(n.purchase_price)}</td>
                    <td style={{ ...s.td, ...s.money, fontWeight: 600 }}>${fmt(n.settlement_amount)}</td>
                    <td style={s.td}>{fmtDate(n.settlement_date)}</td>
                    <td style={s.td}>
                      <button style={s.btnSmall} onClick={() => deal && openPutNotice(deal, n)}>
                        {n.status === 'Settled' || n.status === 'Cancelled' ? 'View' : 'Update'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── FACILITY DETAIL CARDS (multi-tier pricing) ─────────── */}
      {enrichedDeals.length > 0 && (<>
      <div style={s.section}>
        <div style={s.sectionHdr}>
          <h2 style={s.sectionTtl}>Facility Agreements</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Ticker</th>
                <th style={s.th}>Issuer</th>
                <th style={s.th}>Deal</th>
                <th style={s.th}>Investor</th>
                <th style={s.th}>Status</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Commitment</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Max Advance</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Discount %</th>
                <th style={s.th}>Pricing Days</th>
                <th style={s.th}>Pricing Method</th>
                <th style={s.th}>Settlement Days</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Ownership Limit</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Exchange Cap</th>
                <th style={s.th}>S-1 Effective</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrichedDeals.length === 0 ? (
                <tr><td colSpan={15} style={s.emptyRow}>No ELOC facilities found. Create an ELOC security and deal first.</td></tr>
              ) : enrichedDeals.map(d => {
                const sec = d.sec;
                const statusClr = statusColor[sec.eloc_facility_status] || C.muted;
                return (
                  <tr key={d.id} style={{ cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = C.rowHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...s.td, fontWeight: 600, color: C.accent }}>{sec.ticker || '—'}</td>
                    <td style={s.td}>{sec.issuer_name || sec.issuer || '—'}</td>
                    <td style={s.td}>{d.deal_name}</td>
                    <td style={s.td}>{sec.eloc_investor || '—'}</td>
                    <td style={s.td}><span style={s.badge(statusClr)}>{sec.eloc_facility_status || 'N/A'}</span></td>
                    <td style={{ ...s.td, ...s.money }}>${fmt(sec.eloc_commitment_amount)}</td>
                    <td style={{ ...s.td, ...s.money }}>${fmt(sec.eloc_max_advance)}</td>
                    <td style={{ ...s.td, ...s.money }}>{fmtPct(sec.eloc_discount_pct)}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{sec.eloc_pricing_days || '—'}</td>
                    <td style={s.td}>{sec.eloc_pricing_method || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{sec.eloc_settlement_days || '—'}</td>
                    <td style={{ ...s.td, ...s.money }}>{fmtPct(sec.eloc_ownership_limit_pct)}</td>
                    <td style={{ ...s.td, ...s.money }}>{fmtPct(sec.eloc_exchange_cap_pct)}</td>
                    <td style={s.td}>{fmtDate(sec.eloc_s1_effective_date)}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={s.btnSmall} onClick={(e) => { e.stopPropagation(); openEdit(sec); }}>Edit</button>
                        <button style={{ ...s.btnSmall, background: C.orange }} onClick={(e) => { e.stopPropagation(); openPutNotice(d); }}>Put Notice</button>
                        <button style={{ ...s.btnSmall, background: C.purple }} onClick={(e) => { e.stopPropagation(); openCf(d); }}>Book CF</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={s.section}>
        <div style={s.sectionHdr}>
        <h2 style={s.sectionTtl}>Pricing Tiers & Notes</h2>
        </div>
        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
          {enrichedDeals.map(d => {
            const sec = d.sec;
            return (
              <div key={d.id} style={{ background: '#0d1320', border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: C.accent }}>{sec.ticker} — {d.deal_name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12, marginBottom: 10 }}>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>Tier 1 Discount</div>
                    <div style={{ fontWeight: 600 }}>{fmtPct(sec.eloc_discount_pct)}</div>
                  </div>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>Tier 2 Discount</div>
                    <div style={{ fontWeight: 600 }}>{fmtPct(sec.eloc_discount_pct_2)}</div>
                  </div>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>Tier 3 Discount</div>
                    <div style={{ fontWeight: 600 }}>{fmtPct(sec.eloc_discount_pct_3)}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginBottom: 10 }}>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>Commitment Period</div>
                    <div style={{ fontWeight: 600 }}>{sec.eloc_commitment_period_months ? `${sec.eloc_commitment_period_months} months` : '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>Exchange</div>
                    <div style={{ fontWeight: 600 }}>{sec.eloc_exchange || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginBottom: 10 }}>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>CF Shares (Contract)</div>
                    <div style={{ fontWeight: 600, color: C.purple }}>{fmtInt(sec.eloc_commitment_fee_shares)}</div>
                  </div>
                  <div>
                    <div style={{ color: C.muted, marginBottom: 2 }}>CF Amount</div>
                    <div style={{ fontWeight: 600, color: C.purple }}>${fmt(sec.eloc_commitment_fee_amount)}</div>
                  </div>
                </div>
                {sec.eloc_facility_notes && (
                  <div style={{ fontSize: 12, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4 }}>
                    {sec.eloc_facility_notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </>)}

      {/* ── COMMITMENT FEE SHARES TABLE ────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionHdr}>
        <h2 style={s.sectionTtl}>Commitment Fee Share Positions</h2>
        <span style={{ fontSize: 12, color: C.muted }}>{taxLots.length} lot{taxLots.length !== 1 ? 's' : ''}</span>
        </div>
        <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Trade Date</th>
            <th style={s.th}>Deal</th>
            <th style={s.th}>Security</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Shares</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Cost Basis</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Unit Cost</th>
            <th style={s.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {taxLots.length === 0 ? (
            <tr><td colSpan={7} style={s.emptyRow}>No commitment fee share lots booked yet.</td></tr>
          ) : taxLots.map(lot => {
            const deal = dealById[lot.deal];
            const sec = secById[lot.security];
            return (
              <tr key={lot.id}>
                <td style={s.td}>{fmtDate(lot.trade_date)}</td>
                <td style={s.td}>{deal?.deal_name || '—'}</td>
                <td style={{ ...s.td, color: C.accent }}>{sec?.ticker || '—'}</td>
                <td style={{ ...s.td, ...s.money }}>{fmtInt(lot.quantity)}</td>
                <td style={{ ...s.td, ...s.money }}>${fmt(lot.cost_basis)}</td>
                <td style={{ ...s.td, ...s.money }}>${fmt(lot.unit_cost)}</td>
                <td style={s.td}><span style={s.badge(lot.is_open ? C.green : C.muted)}>{lot.is_open ? 'Open' : 'Closed'}</span></td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/*  EDIT FACILITY MODAL                                      */}
      {/* ══════════════════════════════════════════════════════════ */}
      {editModal.open && <Modal
        isOpen={true}
        onClose={() => setEditModal({ open: false, sec: null })}
        title={`Edit Facility — ${editModal.sec?.ticker || ''}`}
        onSubmit={saveEdit}
        submitLabel={saving ? 'Saving…' : 'Save Changes'}
        width={680}
      >
        <FormSection title="Core Terms">
          <FormRow>
            <label>Commitment Amount
              <MF style={s.input} value={editForm.eloc_commitment_amount} onChange={v => setEditForm(f => ({ ...f, eloc_commitment_amount: v }))} />
            </label>
            <label>Max Advance per Draw
              <MF style={s.input} value={editForm.eloc_max_advance} onChange={v => setEditForm(f => ({ ...f, eloc_max_advance: v }))} />
            </label>
          </FormRow>
          <FormRow>
            <label>Pricing Days
              <input style={s.input} type="text" inputMode="numeric" value={editForm.eloc_pricing_days} onChange={e => setEditForm(f => ({ ...f, eloc_pricing_days: e.target.value.replace(/[^0-9]/g, "") }))} />
            </label>
            <label>Settlement Days
              <input style={s.input} type="text" inputMode="numeric" value={editForm.eloc_settlement_days} onChange={e => setEditForm(f => ({ ...f, eloc_settlement_days: e.target.value.replace(/[^0-9]/g, "") }))} />
            </label>
          </FormRow>
          <FormRow>
            <label>Pricing Method
              <input style={s.input} value={editForm.eloc_pricing_method} onChange={e => setEditForm(f => ({ ...f, eloc_pricing_method: e.target.value }))} />
            </label>
            <label>Pricing Method 2
              <input style={s.input} value={editForm.eloc_pricing_method_2} onChange={e => setEditForm(f => ({ ...f, eloc_pricing_method_2: e.target.value }))} />
            </label>
          </FormRow>
        </FormSection>

        <FormSection title="Discount Tiers">
          <FormRow>
            <label>Tier 1 Discount %
              <PF style={s.input} value={editForm.eloc_discount_pct} onChange={v => setEditForm(f => ({ ...f, eloc_discount_pct: v }))} />
            </label>
            <label>Tier 2 Discount %
              <PF style={s.input} value={editForm.eloc_discount_pct_2} onChange={v => setEditForm(f => ({ ...f, eloc_discount_pct_2: v }))} />
            </label>
            <label>Tier 3 Discount %
              <PF style={s.input} value={editForm.eloc_discount_pct_3} onChange={v => setEditForm(f => ({ ...f, eloc_discount_pct_3: v }))} />
            </label>
          </FormRow>
        </FormSection>

        <FormSection title="Limits & Compliance">
          <FormRow>
            <label>Ownership Limit %
              <PF style={s.input} value={editForm.eloc_ownership_limit_pct} onChange={v => setEditForm(f => ({ ...f, eloc_ownership_limit_pct: v }))} />
            </label>
            <label>Exchange Cap %
              <PF style={s.input} value={editForm.eloc_exchange_cap_pct} onChange={v => setEditForm(f => ({ ...f, eloc_exchange_cap_pct: v }))} />
            </label>
          </FormRow>
          <FormRow>
            <label>Commitment Period (months)
              <input style={s.input} type="text" inputMode="numeric" value={editForm.eloc_commitment_period_months} onChange={e => setEditForm(f => ({ ...f, eloc_commitment_period_months: e.target.value.replace(/[^0-9]/g, "") }))} />
            </label>
            <label>S-1 Effective Date
              <input style={s.input} type="date" value={editForm.eloc_s1_effective_date} onChange={e => setEditForm(f => ({ ...f, eloc_s1_effective_date: e.target.value }))} />
            </label>
          </FormRow>
        </FormSection>

        <FormSection title="Commitment Fees">
          <FormRow>
            <label>CF Shares
              <MF style={s.input} value={editForm.eloc_commitment_fee_shares} onChange={v => setEditForm(f => ({ ...f, eloc_commitment_fee_shares: v }))} />
            </label>
            <label>CF Amount
              <MF style={s.input} value={editForm.eloc_commitment_fee_amount} onChange={v => setEditForm(f => ({ ...f, eloc_commitment_fee_amount: v }))} />
            </label>
          </FormRow>
        </FormSection>

        <FormSection title="Counterparty & Status">
          <FormRow>
            <label>Investor
              <input style={s.input} value={editForm.eloc_investor} onChange={e => setEditForm(f => ({ ...f, eloc_investor: e.target.value }))} />
            </label>
            <label>Exchange
              <input style={s.input} value={editForm.eloc_exchange} onChange={e => setEditForm(f => ({ ...f, eloc_exchange: e.target.value }))} />
            </label>
          </FormRow>
          <FormRow>
            <label>Facility Status
              <select style={s.select} value={editForm.eloc_facility_status} onChange={e => setEditForm(f => ({ ...f, eloc_facility_status: e.target.value }))}>
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Expired">Expired</option>
                <option value="Terminated">Terminated</option>
              </select>
            </label>
          </FormRow>
          <label>Facility Notes
            <textarea style={s.textarea} value={editForm.eloc_facility_notes} onChange={e => setEditForm(f => ({ ...f, eloc_facility_notes: e.target.value }))} />
          </label>
        </FormSection>
      </Modal>}

      {/* ══════════════════════════════════════════════════════════ */}
      {/*  BOOK CF SHARES MODAL                                     */}
      {/* ══════════════════════════════════════════════════════════ */}
      {cfModal.open && <Modal
        isOpen={true}
        onClose={() => setCfModal({ open: false, deal: null })}
        title={`Book Commitment Fee Shares — ${cfModal.deal?.deal_name || ''}`}
        onSubmit={saveCf}
        submitLabel={saving ? 'Booking…' : 'Book CF Shares'}
        width={480}
      >
        <FormRow>
          <label>Trade Date
            <input style={s.input} type="date" value={cfForm.trade_date || ''} onChange={e => setCfForm(f => ({ ...f, trade_date: e.target.value }))} />
          </label>
        </FormRow>
        <FormRow>
          <label>Shares
            <MF style={s.input} value={cfForm.quantity || ''} onChange={v => setCfForm(f => ({ ...f, quantity: v }))} />
          </label>
          <label>Cost Basis ($)
            <MF style={s.input} value={cfForm.cost_basis || ''} onChange={v => setCfForm(f => ({ ...f, cost_basis: v }))} />
          </label>
        </FormRow>
        <FormRow>
          <label>Unit Cost ($)
            <MF style={s.input} value={cfForm.unit_cost || ''} onChange={v => setCfForm(f => ({ ...f, unit_cost: v }))} />
          </label>
        </FormRow>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
          Contract specifies {fmtInt(cfModal.deal?.sec?.eloc_commitment_fee_shares)} CF shares for this facility.
        </div>
      </Modal>}

      {/* ══════════════════════════════════════════════════════════ */}
      {/*  PUT NOTICE MODAL                                         */}
      {/* ══════════════════════════════════════════════════════════ */}
      {putModal.open && <Modal
        isOpen={true}
        onClose={() => setPutModal({ open: false, deal: null, notice: null })}
        title={`${putModal.isEdit ? 'Update' : 'New'} Put Notice — ${putModal.deal?.deal_name || ''}`}
        onSubmit={savePutNotice}
        submitLabel={saving ? 'Saving…' : (putModal.isEdit ? 'Update Notice' : 'Submit Put Notice')}
        width={600}
      >
        {/* Info banner showing facility terms */}
        {(() => {
          const sec = secById[putModal.deal?.security] || {};
          return sec.ticker ? (
            <div style={{
              background: '#0d1320', border: `1px solid ${C.border}`, borderRadius: 8,
              padding: 12, marginBottom: 16, fontSize: 12, display: 'flex', gap: 20, flexWrap: 'wrap',
            }}>
              <span><strong>Facility:</strong> {sec.ticker}</span>
              <span><strong>Max Advance:</strong> ${fmt(sec.eloc_max_advance)}</span>
              <span><strong>Discount:</strong> {fmtPct(sec.eloc_discount_pct)}</span>
              <span><strong>Pricing:</strong> {sec.eloc_pricing_days || '?'} days</span>
              <span><strong>Settlement:</strong> T+{sec.eloc_settlement_days || '?'}</span>
            </div>
          ) : null;
        })()}

        <FormSection title="Notice Details">
          <FormRow>
            <label>Notice Date *
              <input style={s.input} type="date" value={putModal.notice?.notice_date || ''}
                onChange={e => updateNoticeField('notice_date', e.target.value)} />
            </label>
            <label>Status *
              <select style={s.select} value={putModal.notice?.status || 'NoticeReceived'}
                onChange={e => updateNoticeField('status', e.target.value)}>
                <option value="NoticeReceived">Notice Received</option>
                <option value="PricingWindow">Pricing Window</option>
                <option value="Settled">Settled</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </label>
          </FormRow>
          <FormRow>
            <label>Requested Shares *
              <MF style={s.input} value={putModal.notice?.requested_shares || ''}
                onChange={e => updateNoticeField('requested_shares', e.target.value)}
                placeholder="Number of shares" />
            </label>
            <label>Drawdown Tier
              <select style={s.select} value={putModal.notice?.drawdown_tier || ''}
                onChange={e => updateNoticeField('drawdown_tier', e.target.value)}>
                <option value="">— Select —</option>
                <option value="1">Tier 1</option>
                <option value="2">Tier 2</option>
                <option value="3">Tier 3</option>
              </select>
            </label>
          </FormRow>
        </FormSection>

        <FormSection title="Pricing Window">
          <FormRow>
            <label>Pricing Start
              <input style={s.input} type="date" value={putModal.notice?.pricing_start || ''}
                onChange={e => updateNoticeField('pricing_start', e.target.value)} />
            </label>
            <label>Pricing End
              <input style={s.input} type="date" value={putModal.notice?.pricing_end || ''}
                onChange={e => updateNoticeField('pricing_end', e.target.value)} />
            </label>
          </FormRow>
          <FormRow>
            <label>VWAP
              <MF style={s.input} value={putModal.notice?.vwap || ''}
                onChange={e => updateNoticeField('vwap', e.target.value)}
                placeholder="Calculated VWAP during pricing window" />
            </label>
            <label>Purchase Price
              <MF style={s.input} value={putModal.notice?.purchase_price || ''}
                onChange={e => updateNoticeField('purchase_price', e.target.value)}
                placeholder="VWAP × (1 - discount)" />
            </label>
          </FormRow>
        </FormSection>

        <FormSection title="Settlement">
          <FormRow>
            <label>Settlement Amount ($)
              <MF style={s.input} value={putModal.notice?.settlement_amount || ''}
                onChange={e => updateNoticeField('settlement_amount', e.target.value)}
                placeholder="Auto-calculates from shares × price" />
            </label>
            <label>Settlement Date
              <input style={s.input} type="date" value={putModal.notice?.settlement_date || ''}
                onChange={e => updateNoticeField('settlement_date', e.target.value)} />
            </label>
          </FormRow>
          {putModal.notice?.settlement_amount && putModal.notice?.requested_shares && (
            <div style={{
              background: '#0d2818', border: `1px solid ${C.green}33`, borderRadius: 6,
              padding: 10, marginTop: 8, fontSize: 12, color: C.green,
              display: 'flex', gap: 24,
            }}>
              <span><strong>Shares:</strong> {fmtInt(putModal.notice.requested_shares)}</span>
              <span><strong>×</strong></span>
              <span><strong>Price:</strong> ${fmt(putModal.notice.purchase_price || putModal.notice.vwap)}</span>
              <span><strong>=</strong></span>
              <span><strong>Total:</strong> ${fmt(putModal.notice.settlement_amount)}</span>
            </div>
          )}
        </FormSection>

        <FormSection title="Notes">
          <label>Notes
            <textarea style={s.textarea} value={putModal.notice?.notes || ''}
              onChange={e => updateNoticeField('notes', e.target.value)}
              placeholder="Optional notes about this put notice..." />
          </label>
        </FormSection>
      </Modal>}
    </div>
  );
}
